import { ConfirmationPageOverlayVariants } from "@/templates/confirmation-page/src/hooks";

import { SupportedLocales, isValidLocale } from "./utils/localization";

export type RouteWithVariant =
    | {
          locale: SupportedLocales;
          featureFlags: Record<
              string,
              boolean | ConfirmationPageOverlayVariants
          >;
      } & (
          | { path: ""; variant: undefined; search: string }
          | {
                path: "software/jira";
                variant:
                    | "paid-lp-jsw-roadmaps"
                    | "paid-lp-jsw-tracker"
                    | "paid-lp-jsw-template"
                    | "paid-lp-jsw-pm"
                    | "paid-lp-jsw-features";
                search: string;
            }
          | {
                path: "software/jira/pricing";
                variant: undefined;
                search: string;
            }
          | {
                path: "software/jira/service-management";
                variant:
                    | "paid-lp-jsm-features"
                    | "paid-lp-jsm-ticketing"
                    | "paid-lp-jsm-service-desk"
                    | "paid-lp-jsm-features-cerebro"
                    | "paid-lp-jsm-itsm"
                    | "paid-lp-jsm-incidentchangemgmt";
                search: string;
            }
          | {
                path: "software/jira/service-management/pricing";
                variant: undefined;
                search: string;
            }
          | {
                path: "software/confluence";
                variant:
                    | "paid-lp-conf-features"
                    | "paid-lp-conf-features-cerebro"
                    | "paid-lp-confl-template"
                    | "paid-lp-confl-documentation";
                search: string;
            }
          | {
                path: "software/confluence/pricing";
                variant: undefined;
                search: string;
            }
          | {
                path: "software/jira/features";
                variant: undefined;
                search: string;
            }
          | {
                path: "software/jira/free";
                variant: undefined;
                search: string;
            }
          | {
                path: "try/cloud/signup";
                variant: undefined;
                search: string;
            }
          | {
                path: "ondemand/signup/checkemail";
                variant: undefined;
                search: string;
            }
          | {
                path: "ondemand/signup/confirmation";
                variant: undefined;
                search: string;
            }
          | {
                path: "jira";
                variant: undefined;
                search: string;
            }
      );

export const getFeatureFlagValues = (params: string[]) => {
    // set defaults for feature flags
    const featureFlags = {
        enableBundlesAfterSignup: false,
        enableCutSignUpStepsInHalf: false,
        enableConfirmationPageOverlay:
            ConfirmationPageOverlayVariants.UNENROLLED,
        provisionSecondPartyApps: false
    };
    params[0] && (params[0] = params[0].replace(/^\?(.+)/, "$1"));
    params.forEach((param) => {
        const [key, value] = param.split("=");
        switch (key) {
            case "provisionSecondPartyApps":
                if (value === "enableProvisionSecondPartyApps") {
                    featureFlags.provisionSecondPartyApps = true;
                }
                break;
            case "bundles_treatment":
                if (value === "bundles_after_signup") {
                    featureFlags.enableBundlesAfterSignup = true;
                }
                break;
            case "enable_cut_sign_up_flow_in_half_variant":
                if (
                    value ===
                    "expcen_2603_enable_cut_sign_up_flow_in_half_variant"
                ) {
                    featureFlags.enableCutSignUpStepsInHalf = true;
                }
                break;
            case "bundles_post_signup_variation_conf":
                if (value === "bundles_post_signup_conf") {
                    featureFlags.enableConfirmationPageOverlay =
                        ConfirmationPageOverlayVariants.BUNDLES_MODAL;
                }
                break;
            case "video_overlay_post_signup_variation_conf":
                if (value === "video_overlay_post_signup_conf") {
                    featureFlags.enableConfirmationPageOverlay =
                        ConfirmationPageOverlayVariants.VIDEO_OVERLAY;
                }
                break;
            case "bundles_post_signup_control_conf":
                if (value === "bundles_post_signup_control_conf") {
                    featureFlags.enableConfirmationPageOverlay =
                        ConfirmationPageOverlayVariants.CONTROL;
                }
                break;
        }
    });
    return featureFlags;
};

export const parseRoute = (location: Location): RouteWithVariant => {
    // remove all leading slashes (server had 2 leading slashes, client has 1)
    const noSlashPrefixPath = location.pathname.replace(/^\/+/, "");

    if (noSlashPrefixPath === "index.html") {
        // case for the unlocalized home page
        return {
            locale: "en",
            path: "",
            variant: undefined,
            search: "",
            featureFlags: {}
        };
    }

    // check if the route has a locale prefix
    const potentialLocale = noSlashPrefixPath.split("/")[0] || "";
    const localeIsValid = isValidLocale(potentialLocale);

    // if there is a locale prefix, set the locale variable and remove the prefix from the path
    // otherwise, set the locale to the default (en) and leave the path as is
    const locale = localeIsValid ? potentialLocale : "en";
    const cleanedPath = localeIsValid
        ? noSlashPrefixPath.slice(locale.length + 1)
        : noSlashPrefixPath;

    const params = location.search.split("&");

    const cmsVariantValue = params
        .filter((param) => param.includes("cmsVariant"))[0]
        ?.split("=")[1];

    const featureFlags = getFeatureFlagValues(params);

    return {
        path: cleanedPath,
        variant: cmsVariantValue,
        locale,
        search: location.search,
        featureFlags
    } as RouteWithVariant;
};
